import { useState, useEffect, useCallback } from "react";
import { Paper, Tooltip } from "@mui/material";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { getInvoiceReviewQueue, reviewInvoice, formatCurrency } from "./Api";
import { InvoiceDetailDrawer } from "./InvoiceDetailDrawer";

const payable = (inv) => (inv?.total != null ? inv.total : inv?.amount);

const REVIEW_TABS = [
  { value: "Pending", label: "Awaiting Approval", icon: "clock", color: "#D97706" },
  { value: "Approved", label: "Approved", icon: "check-circle", color: "#4F46E5" },
  { value: "Declined", label: "Declined", icon: "x-circle", color: "#DC2626" },
];

const fmtSubmitted = (inv) => {
  const d = inv?.createdAt || inv?.invoiceDate;
  return d ? moment(d).format("MMM D, YYYY") : "—";
};

const propertyCell = (inv) => {
  const refs = Array.isArray(inv.properties) ? inv.properties : [];
  if (!refs.length) return "—";
  const first = refs[0];
  const label = `${first.title || "—"}${first.buildingName ? ` · ${first.buildingName}` : ""}`;
  return refs.length > 1 ? `${label} +${refs.length - 1} more` : label;
};

const InvoiceReviewQueue = ({ onAfterReview }) => {
  const [reviewStatus, setReviewStatus] = useState("Pending");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInvoice, setDrawerInvoice] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvoiceReviewQueue({ status: reviewStatus });
      setInvoices(res?.data || []);
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [reviewStatus]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const decide = async (invoice, decision, e) => {
    e?.stopPropagation?.();
    const isAccept = decision === "accept";
    const res = await Swal.fire({
      title: isAccept ? "Approve this invoice?" : "Decline this invoice?",
      text: isAccept
        ? "The property manager will be notified and it moves to the auditors for payment."
        : "The vendor will be notified that the invoice was not approved.",
      input: "textarea",
      inputLabel: isAccept ? "Note (optional)" : "Reason (required)",
      inputPlaceholder: isAccept ? "Add a note for the record…" : "Tell the vendor why…",
      icon: isAccept ? "question" : "warning",
      showCancelButton: true,
      confirmButtonText: isAccept ? "Approve" : "Decline",
      confirmButtonColor: isAccept ? "#059669" : "#DC2626",
      cancelButtonText: "Cancel",
      width: 460,
      inputValidator: (v) =>
        !isAccept && !v?.trim() ? "A reason is required to decline" : undefined,
    });
    if (!res.isConfirmed) return;

    setBusyId(invoice._id);
    try {
      await reviewInvoice(invoice._id, { decision, note: (res.value || "").trim() });
      toast.success(isAccept ? "Invoice approved" : "Invoice declined");
      fetchQueue();
      onAfterReview?.();
    } catch {
      // toast handled in api helper
    } finally {
      setBusyId(null);
    }
  };

  const openPreview = (invoice) => {
    setDrawerInvoice(invoice);
    setDrawerOpen(true);
  };

  return (
    <>
      <Paper elevation={0} className="iv-wrap iv-paper">
        <div className="iv-header">
          <div className="iv-header__left">
            <div className="iv-header__icon-wrap">
              <FeatherIcon icon="inbox" size={18} color="#A5B4FC" />
            </div>
            <div className="iv-header__meta">
              <div className="iv-header__label">Finance</div>
              <div className="iv-header__title">Approvals</div>
            </div>
            {invoices.length > 0 && (
              <div className="iv-header__badges">
                <span className="iv-header__badge iv-header__badge--records">
                  {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {REVIEW_TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setReviewStatus(t.value)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: reviewStatus === t.value ? "#EEF2FF" : "#F8FAFC",
                  border: `1px solid ${reviewStatus === t.value ? "#C7D2FE" : "#E2E8F0"}`,
                  borderRadius: 8, padding: "6px 12px",
                  color: reviewStatus === t.value ? "#4F46E5" : "#64748B",
                  fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
                }}
              >
                <FeatherIcon icon={t.icon} size={13} color={t.color} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="iv-tbl" style={{ minWidth: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: 56 }}>S.No</th>
                <th style={{ minWidth: 120 }}>Submitted On</th>
                <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Vendor</th>
                <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Property / Building</th>
                <th style={{ minWidth: 150, textAlign: "left", paddingLeft: 10 }}>Submitted By</th>
                <th style={{ minWidth: 110, textAlign: "right" }}>Amount</th>
                <th style={{ minWidth: 70 }}>Files</th>
                <th className="iv-actions-th" style={{ width: 170 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: "40px 0" }}>
                    <Spinner style={{ color: "#4F46E5" }} />
                  </td>
                </tr>
              )}

              {!loading && invoices.length === 0 && (
                <tr>
                  <td colSpan={8} className="iv-empty-cell">
                    <div className="iv-empty" style={{ minHeight: "25vh" }}>
                      <div className="iv-empty__icon-wrap">
                        <FeatherIcon icon="inbox" size={24} color="#4F46E5" />
                      </div>
                      <div className="iv-empty__title">Nothing here</div>
                      <div className="iv-empty__sub">
                        {reviewStatus === "Pending"
                          ? "No invoices are waiting for your approval."
                          : `No ${reviewStatus.toLowerCase()} invoices.`}
                      </div>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && invoices.map((inv, idx) => {
                const attachmentCount = inv.attachments?.length || 0;
                return (
                  <tr
                    key={inv._id}
                    className="iv-row"
                    onClick={() => openPreview(inv)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ textAlign: "center" }}>
                      <span style={{ fontWeight: 700, color: "#475569", fontSize: "0.82rem" }}>{idx + 1}</span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span style={{ fontSize: "0.78rem", color: "#475569", fontWeight: 500 }}>{fmtSubmitted(inv)}</span>
                    </td>
                    <td style={{ textAlign: "left", paddingLeft: 10 }}>
                      <div style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>{inv.vendorName || "—"}</div>
                      <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 2 }}>{inv.title || "Untitled"}</div>
                    </td>
                    <td style={{ textAlign: "left", paddingLeft: 10, fontSize: "0.8rem", color: "#0F172A" }}>
                      {propertyCell(inv)}
                    </td>
                    <td style={{ textAlign: "left", paddingLeft: 10, fontSize: "0.8rem", color: "#475569" }}>
                      {inv.createdBy?.displayName || "—"}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 700, color: "#0F172A", fontSize: "0.85rem" }}>
                      {formatCurrency(payable(inv), inv.currency)}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                      {attachmentCount > 0 ? (
                        <span className="iv-chip iv-chip--indigo" style={{ border: "1px solid #C7D2FE" }}>
                          <FeatherIcon icon="paperclip" size={11} />{attachmentCount}
                        </span>
                      ) : (
                        <span style={{ color: "#CBD5E1", fontSize: "0.75rem" }}>—</span>
                      )}
                    </td>
                    <td className="iv-actions-td" onClick={(e) => e.stopPropagation()}>
                      <div className="iv-action-group">
                        <Tooltip title="Preview" arrow>
                          <button type="button" className="iv-ebtn" onClick={() => openPreview(inv)}>
                            <FeatherIcon icon="eye" size={13} color="#0284C7" />
                          </button>
                        </Tooltip>
                        {inv.status === "Pending" && (
                          <>
                            <Tooltip title="Approve" arrow>
                              <button
                                type="button"
                                className="iv-ebtn"
                                style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}
                                disabled={busyId === inv._id}
                                onClick={(e) => decide(inv, "accept", e)}
                              >
                                <FeatherIcon icon="check" size={13} color="#059669" />
                              </button>
                            </Tooltip>
                            <Tooltip title="Decline" arrow>
                              <button
                                type="button"
                                className="iv-dbtn"
                                disabled={busyId === inv._id}
                                onClick={(e) => decide(inv, "reject", e)}
                              >
                                <FeatherIcon icon="x" size={13} color="#DC2626" />
                              </button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      <InvoiceDetailDrawer
        isOpen={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDrawerInvoice(null); }}
        invoice={drawerInvoice}
        canEdit={false}
        onChange={(updated) => setDrawerInvoice(updated)}
      />
    </>
  );
};

export { InvoiceReviewQueue };
